// An invitation link (/accept-invite?token=...) opened while signed out has to survive signing in or
// creating an account. The token is kept in sessionStorage (this tab only) until it is used or
// dismissed, and travels in the confirmation email's link when someone signs up.

import { checkoutQuery } from './checkout';
import { ACCOUNTANT_INVITE_PATH, pendingAccountantInviteToken } from './accountantInvite';
import { PRACTICE_INVITE_PATH, pendingPracticeInviteToken } from './practiceInvite';

const KEY = 'mybooks.inviteToken';
export const INVITE_PATH = '/accept-invite';
const TOKEN_RE = /^[A-Za-z0-9_-]{32,128}$/;

function read(): string | null {
  try { return sessionStorage.getItem(KEY); } catch { return null; }
}

/** The token from the current URL (when on the accept page), else the one kept from earlier. */
export function pendingInviteToken(location: Pick<Location, 'pathname' | 'search'> = window.location): string | null {
  if (location.pathname === INVITE_PATH) {
    const token = new URLSearchParams(location.search).get('token');
    if (token && TOKEN_RE.test(token)) {
      try { sessionStorage.setItem(KEY, token); } catch { /* storage blocked: the URL still has it */ }
      return token;
    }
  }
  const kept = read();
  return kept && TOKEN_RE.test(kept) ? kept : null;
}

export function clearInviteToken() {
  try { sessionStorage.removeItem(KEY); } catch { /* ignore */ }
}

/**
 * Where a sign-up confirmation email should bring the person back to: their invitation (to a company,
 * as its accountant, or to a practice), and the checkout they paid for (auth/checkout.ts) when there
 * is one. The confirmation link opens in a new tab, so the token has to travel in it.
 */
export function signUpRedirect(origin: string = window.location.origin, checkout: string = checkoutQuery()): string {
  const token = read();
  const extra = checkout ? `&${checkout}` : '';
  if (token && TOKEN_RE.test(token)) return `${origin}${INVITE_PATH}?token=${encodeURIComponent(token)}${extra}`;
  const accountant = pendingAccountantInviteToken({ pathname: '', search: '' });
  if (accountant) return `${origin}${ACCOUNTANT_INVITE_PATH}?token=${encodeURIComponent(accountant)}${extra}`;
  const practice = pendingPracticeInviteToken({ pathname: '', search: '' });
  if (practice) return `${origin}${PRACTICE_INVITE_PATH}?token=${encodeURIComponent(practice)}${extra}`;
  return checkout ? `${origin}/?${checkout}` : origin;
}
